import React, { useCallback, useEffect, useRef, useState } from "react";
import { CheckCheck, Inbox, Reply, RotateCcw, Trash2 } from "lucide-react";
import api from "../../../services/api";
import { ConfirmDialog, EmptyState, Loading, errorText, formatDateTime, timeAgo, useToast } from "./common";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "unhandled", label: "Unhandled" },
  { id: "handled", label: "Handled" },
];

const replyHref = (m) => {
  const subject = m.subject ? `Re: ${m.subject}` : "Re: your message to MyEduGenius";
  return `mailto:${m.email}?subject=${encodeURIComponent(subject)}`;
};

const MessagesPanel = ({ intent, onDataChanged }) => {
  const { notify } = useToast();
  const [status, setStatus] = useState(intent?.action === "unhandled-messages" ? "unhandled" : "all");
  const [messages, setMessages] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [rowError, setRowError] = useState({});
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const requestRef = useRef(0);

  useEffect(() => {
    if (intent?.action === "unhandled-messages") setStatus("unhandled");
  }, [intent]);

  const load = useCallback(async () => {
    const req = ++requestRef.current;
    setMessages(null);
    setError(null);
    try {
      const res = await api.get("/admin/messages", { params: { status } });
      if (req === requestRef.current) setMessages(Array.isArray(res.data?.messages) ? res.data.messages : []);
    } catch (err) {
      if (req === requestRef.current) setError(errorText(err, "Couldn't load messages."));
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const setHandled = async (m, handled) => {
    setBusyId(m.id);
    setRowError((e) => ({ ...e, [m.id]: null }));
    try {
      await api.patch(`/admin/messages/${m.id}`, { handled });
      setMessages((list) =>
        status === "all"
          ? list.map((x) => (x.id === m.id ? { ...x, handled } : x))
          : list.filter((x) => x.id !== m.id)
      );
      notify(handled ? "Marked as handled." : "Moved back to unhandled.");
      onDataChanged?.();
    } catch (err) {
      setRowError((e) => ({ ...e, [m.id]: errorText(err, "Couldn't update this message.") }));
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.delete(`/admin/messages/${toDelete.id}`);
      setMessages((list) => list.filter((x) => x.id !== toDelete.id));
      setToDelete(null);
      notify("Message deleted.");
      onDataChanged?.();
    } catch (err) {
      setDeleteError(errorText(err, "Couldn't delete this message."));
    } finally {
      setDeleting(false);
    }
  };

  const emptyTitle =
    status === "unhandled" ? "You're all caught up" : status === "handled" ? "No handled messages" : "No contact messages yet";

  return (
    <>
      <div className="admin-chips" role="group" aria-label="Filter messages">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={`admin-chip ${status === f.id ? "active" : ""}`}
            aria-pressed={status === f.id}
            onClick={() => setStatus(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error ? (
        <div className="admin-error admin-error-row">
          <span>{error}</span>
          <button type="button" className="eg-btn eg-btn-outline" onClick={load}>
            Try again
          </button>
        </div>
      ) : !messages ? (
        <Loading label="Loading messages…" />
      ) : messages.length === 0 ? (
        <div className="dashboard-section">
          <EmptyState icon={Inbox} title={emptyTitle}>
            {status === "unhandled" && "New messages from the Contact page will show up here."}
          </EmptyState>
        </div>
      ) : (
        <div className="admin-messages">
          {messages.map((m) => (
            <article key={m.id} className={`dashboard-section admin-message ${m.handled ? "is-handled" : ""}`}>
              <header>
                <div>
                  <strong>{m.name || "Anonymous"}</strong>
                  {m.email && <a href={`mailto:${m.email}`}>{m.email}</a>}
                </div>
                <div className="admin-message-when">
                  {m.handled ? (
                    <span className="admin-badge admin-badge-success">Handled</span>
                  ) : (
                    <span className="admin-badge admin-badge-warn">New</span>
                  )}
                  <time dateTime={m.created_at} title={formatDateTime(m.created_at)}>
                    {timeAgo(m.created_at) || "—"}
                  </time>
                </div>
              </header>
              {m.subject && <h4>{m.subject}</h4>}
              <p>{m.message}</p>
              <div className="admin-message-actions">
                {m.handled ? (
                  <button
                    type="button"
                    className="eg-btn eg-btn-outline admin-btn-sm"
                    onClick={() => setHandled(m, false)}
                    disabled={busyId === m.id}
                  >
                    <RotateCcw size={14} /> Mark unhandled
                  </button>
                ) : (
                  <button
                    type="button"
                    className="eg-btn eg-btn-primary admin-btn-sm"
                    onClick={() => setHandled(m, true)}
                    disabled={busyId === m.id}
                  >
                    <CheckCheck size={14} /> {busyId === m.id ? "Saving…" : "Mark handled"}
                  </button>
                )}
                {m.email && (
                  <a className="eg-btn eg-btn-outline admin-btn-sm" href={replyHref(m)}>
                    <Reply size={14} /> Reply
                  </a>
                )}
                <button
                  type="button"
                  className="eg-btn admin-btn-danger-outline admin-btn-sm"
                  onClick={() => {
                    setDeleteError(null);
                    setToDelete(m);
                  }}
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
              {rowError[m.id] && <div className="admin-error admin-error-tight">{rowError[m.id]}</div>}
            </article>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={Boolean(toDelete)}
        title="Delete this message?"
        confirmLabel="Delete message"
        busy={deleting}
        error={deleteError}
        onConfirm={doDelete}
        onCancel={() => setToDelete(null)}
      >
        <p>
          The message from <strong>{toDelete?.name || toDelete?.email || "this sender"}</strong> will be deleted
          permanently. If you've dealt with it, you can mark it handled instead.
        </p>
      </ConfirmDialog>
    </>
  );
};

export default MessagesPanel;
